/**
 * Asset Loader Utility
 * 
 * Preloads and manages all image assets for the application.
 */

import { getImageBasePath } from './imagePath.js';

export class AssetLoader {
    constructor() {
        this.images = {};
        this.loadPromises = {};
    }
    
    /**
     * Load a single image
     * @param {string} key - Asset key identifier
     * @param {string} path - Path to image file
     * @returns {Promise<Image>}
     */
    loadImage(key, path) {
        if (this.loadPromises[key]) {
            return this.loadPromises[key];
        }
        
        const promise = new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.images[key] = img;
                resolve(img);
            };
            img.onerror = () => {
                // Don't reject - just log a warning and resolve with null
                // This allows the app to continue even if some images are missing
                console.warn(`Failed to load image: ${path} (key: ${key})`);
                this.images[key] = null;
                resolve(null);
            };
            img.src = path;
        });
        
        this.loadPromises[key] = promise;
        return promise;
    }
    
    /**
     * Load multiple images
     * @param {Object} imageMap - Object mapping keys to paths
     * @returns {Promise<Object>} Object mapping keys to loaded images
     */
    async loadImages(imageMap) {
        const promises = Object.entries(imageMap).map(([key, path]) => 
            this.loadImage(key, path).catch(() => null) // Catch errors and continue
        );
        
        await Promise.allSettled(promises);
        return this.images;
    }
    
    /**
     * Get a loaded image by key
     * @param {string} key - Asset key identifier
     * @returns {Image|null}
     */
    getImage(key) {
        return this.images[key] || null;
    }
    
    /**
     * Check if an image is loaded
     * @param {string} key - Asset key identifier
     * @returns {boolean}
     */
    isLoaded(key) {
        return key in this.images;
    }
    
    /**
     * Preload all application assets
     * @returns {Promise<void>}
     */
    async preloadAll() {
        // Preload sprite sheets for military and resource buildings
        const { getMilbuildingsSpriteSheet, getResbuildingsSpriteSheet } = await import('./SpriteSheet.js');
        
        // Preload military buildings sprite sheet
        const milSpriteSheet = getMilbuildingsSpriteSheet();
        try {
            await milSpriteSheet.load();
        } catch (err) {
            console.warn('Failed to preload milbuildings sprite sheet:', err);
        }
        
        // Preload resource buildings sprite sheet
        const resSpriteSheet = getResbuildingsSpriteSheet();
        try {
            await resSpriteSheet.load();
        } catch (err) {
            console.warn('Failed to preload resbuildings sprite sheet:', err);
        }
        // Use relative path for assets
        // In development: assets/images/
        // In production build: images/ (Vite copies publicDir to dist root)
        const basePath = getImageBasePath();
        
        // Define all asset paths
        const assets = {
            // Backgrounds (legacy - kept for compatibility)
            'bg_land': `${basePath}/bg_land.jpg`,
            'bg_water': `${basePath}/bg_water.jpg`,
            
            // City Design - 3 Layer System
            'city_design_land': `${basePath}/city_design/land.jpg`,
            'city_design_water_water': `${basePath}/city_design/water_water.png`,
            'city_design_wall_wood_l': `${basePath}/city_design/wall-wood-l.png`,
            'city_design_wall_wood_w': `${basePath}/city_design/wall-wood-w.png`,
            'city_design_city_walls': `${basePath}/city_design/city_walls.png`,
            'city_design_walls_water2': `${basePath}/city_design/walls_water2.png`,
            
            // Buildings - Resource Producers
            'building_forester': `${basePath}/buildings/forester-v6-64.png`,
            'building_sawmill': `${basePath}/buildings/sawmill-64.png`,
            'building_quarry': `${basePath}/buildings/quarry-2-64.png`,
            'building_stonemason': `${basePath}/buildings/stonemasonery-64.png`,
            'building_mine': `${basePath}/buildings/iron-mine-64.png`,
            'building_foundry': `${basePath}/buildings/smelter-64.png`,
            'building_farm': `${basePath}/buildings/farm-v3-64.png`,
            'building_mill': `${basePath}/buildings/grain-mill-64-fix.png`,
            
            // Buildings - Infrastructure
            'building_casa': `${basePath}/buildings/basic_house-64.png`,
            'building_cellarium': `${basePath}/buildings/warehouse-v2-64.png`,
            'building_cache': `${basePath}/buildings/hideaway-64.png`,
            'building_townhall': `${basePath}/buildings/basilica-fix-64.png`,
            
            // Buildings - Commerce
            'building_wharf': `${basePath}/buildings/ports-front-flip-64.png`,
            'building_bazaar': `${basePath}/buildings/market-64.png`,
            'building_manor': `${basePath}/buildings/villa-64.png`,
            
            // Buildings - Military
            'building_barracks': `${basePath}/buildings/barracks-v2-64.png`,
            'building_castle': `${basePath}/buildings/castle-64-fix.png`,
            'building_guard_house': `${basePath}/buildings/guard's_house-v2-64.png`,
            'building_moonglow': `${basePath}/buildings/sorcerer's-tower-v2-64.png`,
            'building_stable': `${basePath}/buildings/stable-v2-fix-64.png`,
            'building_temple': `${basePath}/buildings/academy.png`,
            'building_training_ground': `${basePath}/buildings/training_arena-64.png`,
            'building_workshop': `${basePath}/buildings/blacksmith-v2-64.png`,
            'building_shipyard': `${basePath}/buildings/shipyard-front-flip-64.png`,
            
            // Resource Nodes
            'resource_wood': `${basePath}/resources/wood.png`,
            'resource_stone': `${basePath}/resources/stone.png`,
            'resource_iron': `${basePath}/resources/iron.png`,
            'resource_lake': `${basePath}/buildings/lake-2-64.png`,
            'resource_forest': `${basePath}/buildings/forest-2-64.png`,
            'resource_stones': `${basePath}/buildings/stones-3-64.png`,
            'resource_iron_node': `${basePath}/buildings/iron-2-64.png`,
            
            // Buildings (Core)
            'building_townhall': `${basePath}/buildings/basilica-fix-64.png`,
            'building_forester': `${basePath}/buildings/forester-v6-64.png`,
            'building_sawmill': `${basePath}/buildings/sawmill-64.png`,
            'building_quarry': `${basePath}/buildings/quarry-2-64.png`,
            'building_stonemason': `${basePath}/buildings/stonemasonery-64.png`,
            'building_mine': `${basePath}/buildings/iron-mine-64.png`,
            'building_foundry': `${basePath}/buildings/smelter-64.png`,
            'building_farm': `${basePath}/buildings/farm-v3-64.png`,
            'building_mill': `${basePath}/buildings/grain-mill-64-fix.png`,
            'building_townhouse': `${basePath}/buildings/villa-64.png`,
            'building_market': `${basePath}/buildings/market-64.png`,
            'building_harbor': `${basePath}/buildings/ports-front-flip-64.png`,
            'building_casa': `${basePath}/buildings/basic_house-64.png`,
            'building_cellarium': `${basePath}/buildings/warehouse-v2-64.png`,
            'building_cache': `${basePath}/buildings/hideaway-64.png`,
            
            // Military Buildings
            'building_barracks': `${basePath}/buildings/barracks-v2-64.png`,
            'building_castle': `${basePath}/buildings/castle-64-fix.png`,
            'building_guard_house': `${basePath}/buildings/guard's_house-v2-64.png`,
            'building_training_ground': `${basePath}/buildings/training_arena-64.png`,
            'building_stable': `${basePath}/buildings/stable-v2-fix-64.png`,
            'building_moonglow': `${basePath}/buildings/sorcerer's-tower-v2-64.png`,
            'building_workshop': `${basePath}/buildings/blacksmith-v2-64.png`,
            'building_shipyard': `${basePath}/buildings/shipyard-front-flip-64.png`,
            'building_temple': `${basePath}/buildings/academy.png`,
            
            // UI Elements
            'tile_buildable': `${basePath}/ui/tile_buildable.png`,
            'tile_empty': `${basePath}/ui/tile_empty.png`,
            'tile_cursor': `${basePath}/ui/tile_cursor.png`,
            'cursor_1x1': `${basePath}/ui/cursor_1x1.png`,
            'cursor_1x3': `${basePath}/ui/cursor_1x3.png`,
            'cursor_2x3': `${basePath}/ui/cursor_2x3.png`,
            'cursor_3x1': `${basePath}/ui/cursor_3x1.png`,
            'cursor_3x2': `${basePath}/ui/cursor_3x2.png`,
            'cursor_3x3': `${basePath}/ui/cursor_3x3.png`,
            'locked': `${basePath}/ui/locked.png`,
            'locked_32': `${basePath}/ui/locked_32x32.png`,
            'unlocked': `${basePath}/ui/unlocked.png`,
            'delete': `${basePath}/ui/delete.png`,
            'star_icon': `${basePath}/ui/star_icon.png`,
            'keyboard_icon': `${basePath}/ui/keyboard-icon.png`,
            'summary_grid': `${basePath}/ui/summary_grid.png`,
            
            // Icons
            'icon_build_slots': `${basePath}/ui/icon_build_slots.png`,
            'icon_cart': `${basePath}/ui/icon_cart_city_info.png`,
            'icon_ship': `${basePath}/ui/icon_ship_city_info.png`,
            'icon_clock': `${basePath}/ui/icon_clock_orange.png`,
            'icon_resource_wood': `${basePath}/ui/icon_resource_wood.png`,
            'icon_resource_stone': `${basePath}/ui/icon_resource_stone.png`,
            'icon_resource_iron': `${basePath}/ui/icon_resource_iron.png`,
            'icon_resource_food': `${basePath}/ui/icon_resource_grain.png`,
            'icon_resource_gold': `${basePath}/ui/icon_resource_gold.png`,
            
            // Farm bonus indicators
            'wheat': `${basePath}/buildings/wheat.png`,
        };
        
        console.log('Loading assets...');
        await this.loadImages(assets);
        
        // Count successfully loaded images
        const loadedCount = Object.values(this.images).filter(img => img !== null).length;
        const totalCount = Object.keys(assets).length;
        console.log(`Loaded ${loadedCount}/${totalCount} assets`);
        
        if (loadedCount < totalCount) {
            console.warn(`Some assets failed to load. Check console for details.`);
        }
        
        return this.images;
    }
}

// Export singleton instance
export const assetLoader = new AssetLoader();

